import { computed, ref } from 'vue'

import { useOnboardingTour } from '@/composition/user/useOnboardingTour'
import { Scorecard, ValueOf } from '@opteo/types'

import { authRequest, Endpoint, useAPI } from '../../api/useAPI'
import { useScorecardSingle } from './useScorecardSingle'
import { useBoringAvatar } from '@/composition/user/useBoringAvatar'
import { useAccount } from '@/composition/account/useAccount'

const showEditCoverModal = ref(false)

export function useScorecardCoverEditModal() {
    const { accountId } = useAccount()
    const { scorecard, scorecardId, loadingScorecard, mutateScorecard } = useScorecardSingle()
    const { currentUserAvatarUrl } = useBoringAvatar()

    const {
        data: generalCoverPageData,
        mutate: mutateGeneralCoverPageData,
        loading: isLoadingGeneralCoverPageData,
    } = useAPI<Scorecard.CoverPageData>(Endpoint.GetCoverPageData, {
        uniqueId: () => accountId.value,
        waitFor: () => accountId.value,
    })

    const isSingleScorecard = computed(() => !!scorecardId.value)

    const coverPageDataLoading = computed(() => {
        if (isSingleScorecard.value) {
            return loadingScorecard.value
        } else {
            return isLoadingGeneralCoverPageData.value
        }
    })

    const coverPageData = computed(() => {
        if (isSingleScorecard.value) {
            const scorecardSpecificCoverPageData = scorecard.value?.body.coverPageData

            return scorecardSpecificCoverPageData
        } else {
            return generalCoverPageData.value
        }
    })

    const submitAccountInfoForm = ref()
    const senderFormSubmitFunction = ref()

    const registerAccountInfoSubmitFunction = (submitFunction: () => {}) => {
        submitAccountInfoForm.value = submitFunction
    }

    const registerSenderInfoFormSubmitFunction = (submitFunction: () => {}) => {
        senderFormSubmitFunction.value = submitFunction
    }

    // Mini cover edit modals
    const accountEditModalOpen = ref(false)
    const senderEditModalOpen = ref(false)
    const hasUploadedSenderLogo = ref(false)

    const extractAccountInfo = () => {
        const accountInfo: Partial<Scorecard.CoverPageDataWithoutSender> =
            submitAccountInfoForm.value()

        return accountInfo
    }

    const extractSenderInfo = () => {
        const submittedSenderInfo: Partial<Scorecard.ScorecardSenderInfo> =
            senderFormSubmitFunction.value()

        if (!submittedSenderInfo?.senderEmail && !scorecard.value?.autoGenerated) {
            throw new Error('Sender email is required')
        }

        if (
            typeof submittedSenderInfo?.senderEmail === 'undefined' &&
            scorecard.value?.autoGenerated
        ) {
            throw new Error(
                'Sender email is invalid, please enter a valid email address or leave empty.'
            )
        }
        /**
         * For auto-generated scorecards, unless a sender logo is uploaded,
         * add the current user's avatar full url to the cover page data's sender info upon editing the scorecard sender settings.
         */
        const updatedSenderInfo: Partial<Scorecard.ScorecardSenderInfo> =
            scorecard.value?.autoGenerated && !hasUploadedSenderLogo.value
                ? {
                      ...submittedSenderInfo,
                      senderLogoUrl: currentUserAvatarUrl?.value,
                  }
                : submittedSenderInfo

        return updatedSenderInfo
    }

    const updateAccountInfo = async () => {
        const accountInfo = extractAccountInfo()
        await updateScorecardSettings(accountInfo)
        accountEditModalOpen.value = false
    }

    const updateSenderInfo = async () => {
        const senderInfo = extractSenderInfo()
        await updateScorecardSettings(senderInfo)
        senderEditModalOpen.value = false
    }

    const updateScorecardSettings = async (settings: Partial<Scorecard.CoverPageData>) => {
        await authRequest(Endpoint.SetCoverPageData, {
            body: { account_id: accountId.value, scorecardId: scorecardId.value, ...settings },
        })
        isSingleScorecard.value ? await mutateScorecard() : await mutateGeneralCoverPageData()
    }

    const submitScorecardSettings = async () => {
        const accountInfo = extractAccountInfo()
        const senderInfo = extractSenderInfo()

        const updateObject: Partial<Scorecard.CoverPageData> = {
            ...accountInfo,
            ...senderInfo,
        }

        await updateScorecardSettings(updateObject)

        showEditCoverModal.value = false
    }

    const handleLogoUpdate = async <T extends Scorecard.CoverPageData>(
        updatedProp: keyof T,
        updatedValue: ValueOf<T>
    ) => {
        if (!coverPageData.value) {
            return
        }

        const updatedCoverPageData: Scorecard.CoverPageData = {
            ...coverPageData.value,
            [updatedProp]: updatedValue,
        }

        // Handle update if updating from Scorecard List
        if (!isSingleScorecard.value) {
            return mutateGeneralCoverPageData(() => ({ ...updatedCoverPageData }))
        }

        // Handle update if updating from Single Scorecard
        if (!scorecard.value) return

        const updatedScorecard: Scorecard.SingleScorecardFull = {
            ...scorecard.value,
            body: {
                ...scorecard.value.body,
                coverPageData: updatedCoverPageData,
            },
        }

        mutateScorecard(() => ({ ...updatedScorecard }))
        await updateScorecardSettings({ ...updatedCoverPageData })

        return
    }

    const updateAccountLogoUrl = (url: string | null) => {
        handleLogoUpdate('accountLogoUrl', url)
    }

    const updateSenderLogoUrl = (url: string | null) => {
        hasUploadedSenderLogo.value = !!url

        handleLogoUpdate('senderLogoUrl', url ?? currentUserAvatarUrl?.value)
    }

    const { modalVisible: showCoverEditOnboarding, dismiss: setCoverEditOnboardingSeen } =
        useOnboardingTour({
            stepName: 'tour_scorecard_cover',
        })

    return {
        coverPageData,
        coverPageDataLoading,

        registerAccountInfoSubmitFunction,
        registerSenderInfoFormSubmitFunction,

        showEditCoverModal,
        accountEditModalOpen,
        senderEditModalOpen,

        updateAccountInfo,
        updateAccountLogoUrl,
        updateSenderInfo,
        updateSenderLogoUrl,
        submitScorecardSettings,

        showCoverEditOnboarding,
        setCoverEditOnboardingSeen,
    }
}
